import { message, notification } from "antd";
import {
    fetchPerson,
    searchPeople,
    fetchPeopleListsV2,
    createPeopleListV2,
    addPeopleToListV2,
    fetchPeopleFromProductList,
    fetchPeopleFromProductListBoth,
    fetchPeopleFromProducts,
    fetchPeopleFromProductsBoth,
    fetchPhone,
    fetchWorkEmail,
} from "./service";
import { validateCredits } from "@/utils/credits";
import { Effect, Reducer, AnyAction } from '@umijs/max';
import { request } from '@umijs/max';
import { fixed_companies } from '../helper/fixed_companies';

export interface PersonSearchRequest {
    page?: number;
    role_title?: string;
    person_locations?: number[];
    person_name?: string;
    organizations?: number[];
    organization_employees_start?: number;
    organization_employees_end?: number;
    organization_revenue_start?: number;
    organization_revenue_end?: number;
    organization_industries?: number[];
    role_is_current?: boolean;
    person_skills?: number[];
    funding_types?: string[];
    funding_total_start?: number;
    funding_total_end?: number;
}

export interface SimplifiedPerson {
    record_id: number;
    person_id: number;
    person_photo: string | null;
    person_gender: string | null;
    name: string;
    role_title: string;
    location: string | null;
    linkedin_url: string | null;
    organization_id: number;
    organization_name: string;
    organization_legal_name: string;
    organization_logo: string | null;
    organization_employees_range: string | null;
    organization_employees_amount: number | null;
    organization_domain: string | null;
    viewed?: boolean;
    emails: string[] | string | null;
    work_email: string | null;
    work_email_status: string | null;
    date_updated: string | null;
    email_fetched_from: string | null;
    phone_numbers: string[] | null;
}

export interface SimplifiedPersonSearchResponse {
    search_results: SimplifiedPerson[];
    total_search_results: number;
}

interface PeopleModelState {
    searchValue: string;
    searchType: 'search_people' | 'search_company';
    filters: Partial<PersonSearchRequest>;
    peopleData: {
        pages: {
            [key: number]: {
                data: SimplifiedPerson[];
                total: number;
            }
        };
        total: number;
    };
    currentPage: number;
    pageSize: number;
    selectedRows: number[];
    filterDrawerVisible: boolean;
    peopleLists: any[];
    listModalVisible: boolean;
    createListModalVisible: boolean;
    addingToList: boolean;
    organizationOptions: Array<{ value: string, label: string }>;
    companies: Array<{ name: string, foragerId: string }>;
}

interface PeopleModelType {
    namespace: 'people';
    state: PeopleModelState;
    effects: {
        fetchPeople: Effect;
        fetchPerson: Effect;
        fetchPeopleListsV2: Effect;
        createPeopleList: Effect;
        addPeopleToList: Effect;
        addAllPeopleToSelectedRows: Effect;
        getSelectedPeopleFromIds: Effect;
        fetchPeopleFromProductList: Effect;
        fetchPeopleFromProductListBoth: Effect;
        fetchPeopleFromProducts: Effect;
        fetchPeopleFromProductsBoth: Effect;
        changePage: Effect;
        fetchPhone: Effect;
        fetchWorkEmail: Effect;
        fetchCompanies: Effect;
    };
    reducers: {
        updateState: Reducer<PeopleModelState>;
        updateSearchValue: Reducer<PeopleModelState>;
        updateSearchValueAndOrganization: Reducer<PeopleModelState>;
        updateSearchValueOnly: Reducer<PeopleModelState>;
        updateSearchType: Reducer<PeopleModelState>;
        setPersonsForPage: Reducer<PeopleModelState>;
        resetFilters: Reducer<PeopleModelState>;
        toggleFilterDrawer: Reducer<PeopleModelState>;
        updateFilter: Reducer<PeopleModelState>;
        addToSelectedRows: Reducer<PeopleModelState>;
        removeFromSelectedRows: Reducer<PeopleModelState>;
        clearSelectedRows: Reducer<PeopleModelState>;
        setSelectedRows: Reducer<PeopleModelState>;
        setPeopleLists: Reducer<PeopleModelState>;
        toggleListModal: Reducer<PeopleModelState>;
        toggleCreateListModal: Reducer<PeopleModelState>;
        setAddingToList: Reducer<PeopleModelState>;
        setCompanies: Reducer<PeopleModelState>;
        resetAll: Reducer<PeopleModelState>;
    };
}

const Model: PeopleModelType = {
    namespace: 'people',
    state: {
        searchValue: '',
        searchType: 'search_company',
        filters: {},
        peopleData: {
            pages: {},
            total: 0
        },
        currentPage: 1,
        pageSize: 10,
        selectedRows: [],
        filterDrawerVisible: false,
        peopleLists: [],
        listModalVisible: false,
        createListModalVisible: false,
        addingToList: false,
        organizationOptions: [],
        companies: [],
    },
    effects: {
        *fetchPeople({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);

                const current = 1;

                const searchParams: PersonSearchRequest = {
                    ...state.filters,
                    ...(payload as Partial<PersonSearchRequest>),
                    page: current,
                };

                if(state.searchType === 'search_people' && state.searchValue && state.searchValue.length > 0) {
                    searchParams.person_name = state.searchValue;
                }

                // Modified check: ignore role_is_current when checking if all params are empty
                const hasValidParams = Object.entries(searchParams).some(([key, value]) => {
                    if (key === 'role_is_current' || key === 'page' || key === '_timestamp') return false;
                    if (Array.isArray(value) && value.length > 0) return true;
                    if (value !== undefined && value !== null && value !== '') return true;
                    return false;
                });

                if (!hasValidParams) {
                    message.error('Please provide search terms or apply filters before searching.');
                    return;
                }

                // Reset peopleData before fetching new results
                yield put({
                    type: 'updateState',
                    payload: {
                        peopleData: {
                            pages: {},
                            total: 0
                        }
                    }
                });

                yield put({
                    type: 'clearSelectedRows'
                });

                const response: SimplifiedPersonSearchResponse = yield call(searchPeople, searchParams);

                console.log('response', response);

                yield put({
                    type: 'setPersonsForPage',
                    payload: {
                        page: current,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        currentPage: current
                    }
                });
            } catch (error) {
                if (error && (error as any).response && (error as any).response.status === 400) {
                    notification.error({
                        message: 'Invalid Search',
                        description: (error as any).response.data.message,
                    });
                } else {
                    message.error('Failed to fetch people data');
                }
                console.log('Error fetching people:', error);
            }
        },
        *changePage({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);
                const targetPage = payload.page;

                if (state.peopleData.pages[targetPage]?.data?.length > 0) {
                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: targetPage
                        }
                    });
                    return;
                }

                const searchParams: PersonSearchRequest = {
                    ...state.filters,
                    page: targetPage,
                };

                if (state.searchType === 'search_people') {
                    searchParams.person_name = state.searchValue;
                }

                const response: SimplifiedPersonSearchResponse = yield call(searchPeople, searchParams);

                yield put({
                    type: 'setPersonsForPage',
                    payload: {
                        page: targetPage,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        currentPage: targetPage
                    }
                });
            } catch (error) {
                message.error('Failed to fetch people data for page');
                console.error('Error fetching people for page:', error);
            }
        },
        *fetchPerson({ callback, payload, isFreePlan }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                let state = yield select((state: any) => state.people);
                const { peopleData } = state;

                const isViewed = Object.values(peopleData.pages).some((pageData: any) => {
                    return pageData.data.some((person: SimplifiedPerson) =>
                        person.person_id === payload && person.viewed
                    );
                });

                if (isViewed) {
                    callback?.();
                    return;
                }

                const { people_credits_remaining } = yield select((state: any) => state.subscription);
                if (!validateCredits(people_credits_remaining, 'person')) {
                    callback?.();
                    return;
                }

                const response: SimplifiedPerson = yield call(fetchPerson, payload, isFreePlan);

                state = yield select((state: any) => state.people);
                const { currentPage } = state;
                const pageData = peopleData.pages[currentPage]?.data || [];

                const updatedPageData = pageData.map((person: SimplifiedPerson) => {
                    if (person.person_id === response.person_id) {
                        return {
                            ...person,
                            ...response,
                            viewed: true
                        };
                    }
                    return person;
                });

                yield put({
                    type: 'setPersonsForPage',
                    payload: {
                        page: currentPage,
                        data: updatedPageData,
                        total: peopleData.total
                    }
                });

                callback?.();

                if (response.work_email) {
                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: people_credits_remaining - 5
                        }
                    });
                }

                // DEBUG ONLY:
                // if (response.work_email) {
                //     message.info('Fetched from: ' + response.email_fetched_from);
                // }


            } catch (error) {
                message.error('Failed to fetch person data');
                console.error('Error fetching person:', error);
            }
        },
        *fetchPhone({ payload, callback }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                // If phone numbers already exist, no need to refetch
                if (payload.phone_numbers && payload.phone_numbers.length > 0) {
                    callback?.();
                    return;
                }

                const { people_credits_remaining } = yield select((state: any) => state.subscription);
                if (!validateCredits(people_credits_remaining, 'phone')) {
                    callback?.();
                    return;
                }

                const response: any = yield call(fetchPhone, payload);

                if (response.success) {
                    let state = yield select((state: any) => state.people);
                    const { peopleData, currentPage } = state;
                    const pageData = peopleData.pages[currentPage]?.data || [];

                    const updatedPageData = pageData.map((person: SimplifiedPerson) => {
                        if (person.person_id === payload.person_id) {
                            return {
                                ...person,
                                phone_numbers: response.phone_numbers
                            };
                        }
                        return person;
                    });

                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: currentPage,
                            data: updatedPageData,
                            total: peopleData.total
                        }
                    });

                    callback?.();

                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: people_credits_remaining - 10
                        }
                    });
                } else {
                    message.error('No mobile number found');
                    callback?.();
                }

                console.log('fetchPhone response', response);
            } catch (error) {
                message.error('No mobile number found');
                callback?.();
            }
        },
        *fetchWorkEmail({ payload, callback }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const hasWorkEmail = payload.work_email && payload.work_email !== '-' && !String(payload.work_email).includes('*');
                if (hasWorkEmail) {
                    callback?.();
                    return;
                }

                const { people_credits_remaining } = yield select((state: any) => state.subscription);
                if (!validateCredits(people_credits_remaining, 'email')) {
                    callback?.();
                    return;
                }

                const response: any = yield call(fetchWorkEmail, { person_id: payload.person_id });

                if (response?.success && response.work_email) {
                    let state = yield select((state: any) => state.people);
                    const { peopleData, currentPage } = state;
                    const pageData = peopleData.pages[currentPage]?.data || [];

                    const updatedPageData = pageData.map((person: SimplifiedPerson) => {
                        if (person.person_id === payload.person_id) {
                            return {
                                ...person,
                                work_email: response.work_email,
                                work_email_status: response.work_email_status ?? person.work_email_status
                            };
                        }
                        return person;
                    });

                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: currentPage,
                            data: updatedPageData,
                            total: peopleData.total
                        }
                    });

                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: people_credits_remaining - 5
                        }
                    });
                } else {
                    message.error('No work email found');
                }
                callback?.();
            } catch (error) {
                message.error('No work email found');
                callback?.();
            }
        },
        *fetchPeopleListsV2(_: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                const response: any[] = yield call(fetchPeopleListsV2);

                yield put({
                    type: 'setPeopleLists',
                    payload: response
                });
            } catch (error) {
                message.error('Failed to fetch people lists');
                console.error('Error fetching people lists:', error);
            }
        },
        *createPeopleList({ payload, callback }: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                const response: any = yield call(createPeopleListV2, { name: payload.name });

                yield put({
                    type: 'fetchPeopleListsV2'
                });

                if (callback) {
                    callback(response);
                }

                yield put({
                    type: 'toggleCreateListModal',
                    payload: false
                });

                message.success('List created successfully');
            } catch (error) {
                message.error('Failed to create list');
                console.error('Error creating list:', error);
            }
        },
        *addPeopleToList({ payload, callback }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                yield put({
                    type: 'setAddingToList',
                    payload: true
                });

                const response: any = yield call(addPeopleToListV2, {
                    listId: payload.listId,
                    peopleIds: payload.peopleIds
                });

                if (response.credits_to_deduct > 0) {
                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: (yield select((state: any) =>
                                state.subscription.people_credits_remaining
                            )) - response.credits_to_deduct
                        }
                    });
                }

                // Get people IDs from response
                const peopleFromResponse = response.people || [];
                const updatedPeopleIds = peopleFromResponse.map((p: SimplifiedPerson) => p.person_id);

                // Get all the peopleData
                const peopleData = yield select((state: any) => state.people.peopleData);

                // Create deep copy of pages to safely modify
                const updatedPages = JSON.parse(JSON.stringify(peopleData.pages));

                // Update viewed status for each person across all pages
                Object.keys(updatedPages).forEach(pageKey => {
                    const page = updatedPages[pageKey];
                    if (page && page.data) {
                        updatedPages[pageKey].data = page.data.map((person: SimplifiedPerson) => {
                            if (updatedPeopleIds.includes(person.person_id)) {
                                // Find this person in the response data for complete information
                                const updatedPerson = peopleFromResponse.find(
                                    (p: SimplifiedPerson) => p.person_id === person.person_id
                                );

                                // Merge with response data if available, always set viewed: true
                                return {
                                    ...person,
                                    ...(updatedPerson || {}),
                                    viewed: true
                                };
                            }
                            return person;
                        });
                    }
                });

                // Update state with modified data
                yield put({
                    type: 'updateState',
                    payload: {
                        peopleData: {
                            ...peopleData,
                            pages: updatedPages
                        }
                    }
                });

                yield put({
                    type: 'toggleListModal',
                    payload: false
                });

                yield put({
                    type: 'clearSelectedRows'
                });

                yield put({
                    type: 'setAddingToList',
                    payload: false
                });

                if (callback) {
                    callback(response);
                }

                if (response && response.message) {
                    message.success(response.message);
                } else {
                    message.success(`Added ${payload.peopleIds.length} people to list`);
                }
            } catch (error) {
                yield put({
                    type: 'setAddingToList',
                    payload: false
                });

                message.error('Failed to add people to list');
                console.error('Error adding people to list:', error);
            }
        },
        *addAllPeopleToSelectedRows({ payload }: AnyAction, { put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);
                const allRows: number[] = [];

                Object.values(state.peopleData.pages).forEach((page: any) => {
                    page.data.forEach((person: SimplifiedPerson) => {
                        allRows.push(person.person_id);
                    });
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        selectedRows: [...new Set(allRows)]
                    }
                });
            } catch (error) {
                console.error('Error adding all people to selected rows:', error);
            }
        },
        *getSelectedPeopleFromIds({ callback }: AnyAction, { select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);
                const { selectedRows, peopleData } = state;

                // Get all people data from all pages
                const allPeople: SimplifiedPerson[] = [];
                Object.values(peopleData.pages).forEach((page: any) => {
                    page.data.forEach((person: SimplifiedPerson) => {
                        if (selectedRows.includes(person.person_id)) {
                            allPeople.push(person);
                        }
                    });
                });

                if (callback) {
                    callback(allPeople);
                }
            } catch (error) {
                console.error('Error getting selected people:', error);
                if (callback) {
                    callback([]);
                }
            }
        },
        *fetchPeopleFromProductList({ payload, look_for }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const response: any = yield call(fetchPeopleFromProductList, {
                    product_list_item_ids: payload,
                    look_for: look_for
                });
                // Extract people and marketers from the response
                const { people, marketers, manufacturers } = response;

                if (look_for === 'marketer') {
                    if (marketers && marketers.length > 0) {
                        // Create organization filter options from marketers
                        const organizationIds = marketers.map((marketer: any) => marketer.forager_id);

                        // Create options for display in the Select component
                        const options = marketers.map((marketer: any) => ({
                            value: marketer.forager_id,
                            label: marketer.name || marketer.domain || marketer.forager_id,
                        }));

                        // Store the organization options
                        yield put({
                            type: 'updateState',
                            payload: {
                                organizationOptions: options
                            }
                        });

                        // Set the organizations filter value
                        yield put({
                            type: 'updateState',
                            payload: {
                                filters: {
                                    ...((yield select((state: any) => state.people.filters)) || {}),
                                    organizations: organizationIds
                                }
                            }
                        });
                    }
                } else if (look_for === 'manufacturer') {
                    if (manufacturers && manufacturers.length > 0) {
                        // Create organization filter options from manufacturers
                        const organizationIds = manufacturers.map((manufacturer: any) => manufacturer.forager_id);

                        // Create options for display in the Select component
                        const options = manufacturers.map((manufacturer: any) => ({
                            value: manufacturer.forager_id,
                            label: manufacturer.name || manufacturer.forager_id,
                        }));

                        // Store the organization options
                        yield put({
                            type: 'updateState',
                            payload: {
                                organizationOptions: options
                            }
                        });

                        // Set the organizations filter value
                        yield put({
                            type: 'updateState',
                            payload: {
                                filters: {
                                    ...((yield select((state: any) => state.people.filters)) || {}),
                                    organizations: organizationIds
                                }
                            }
                        });
                    }
                }

                // If we have people data, update the state with it
                if (people && people.search_results.length > 0) {
                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: 1,
                            data: people.search_results,
                            total: people.total_search_results
                        }
                    });

                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: 1
                        }
                    });
                } else {
                    // If no people data came with the response, fetch people with the updated filters
                    yield put({
                        type: 'fetchPeople',
                        payload: {
                            page: 0
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching people from product list:', error);
            }
        },
        *fetchPeopleFromProductListBoth({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                console.log('fetchPeopleFromProductListBoth', payload);
                const response: any = yield call(fetchPeopleFromProductListBoth, {
                    product_list_item_ids: payload
                });
                
                // Extract people, marketers, and manufacturers from the response
                const { people, marketers, manufacturers, summary } = response;
                
                console.log('Product list both response summary:', summary);

                // Combine marketers and manufacturers for organization options
                const allOrganizations = [];
                
                if (marketers && marketers.length > 0) {
                    const marketerOptions = marketers.map((marketer: any) => ({
                        value: marketer.forager_id,
                        label: marketer.name || marketer.domain || marketer.forager_id,
                        type: 'marketer'
                    }));
                    allOrganizations.push(...marketerOptions);
                }

                if (manufacturers && manufacturers.length > 0) {
                    const manufacturerOptions = manufacturers.map((manufacturer: any) => ({
                        value: manufacturer.forager_id,
                        label: manufacturer.name || manufacturer.forager_id,
                        type: 'manufacturer'
                    }));
                    allOrganizations.push(...manufacturerOptions);
                }

                // Remove duplicates (in case a company is both marketer and manufacturer)
                const uniqueOrganizations = allOrganizations.filter((org, index, arr) => 
                    arr.findIndex(item => item.value === org.value) === index
                );

                if (uniqueOrganizations.length > 0) {
                    // Store the organization options
                    yield put({
                        type: 'updateState',
                        payload: {
                            organizationOptions: uniqueOrganizations
                        }
                    });

                    // Set the organizations filter value
                    yield put({
                        type: 'updateState',
                        payload: {
                            filters: {
                                ...((yield select((state: any) => state.people.filters)) || {}),
                                organizations: uniqueOrganizations.map(org => org.value)
                            }
                        }
                    });
                }

                // If we have people data, update the state with it
                if (people && people.search_results.length > 0) {
                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: 1,
                            data: people.search_results,
                            total: people.total_search_results
                        }
                    });

                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: 1
                        }
                    });
                } else {
                    // If no people data came with the response, fetch people with the updated filters
                    yield put({
                        type: 'fetchPeople',
                        payload: {
                            page: 0
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching people from product list (both):', error);
            }
        },
        *fetchPeopleFromProducts({ payload, look_for }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {

                console.log('fetchPeopleFromProducts', payload, look_for);
                const response: any = yield call(fetchPeopleFromProducts, {
                    product_ids: payload,
                    look_for: look_for
                });
                // Extract people and marketers from the response
                const { people, marketers, manufacturers } = response;

                if (look_for === 'marketer') {
                    if (marketers && marketers.length > 0) {
                        // Create organization filter options from marketers
                        const organizationIds = marketers.map((marketer: any) => marketer.forager_id);

                        // Create options for display in the Select component
                        const options = marketers.map((marketer: any) => ({
                            value: marketer.forager_id,
                            label: marketer.name || marketer.domain || marketer.forager_id,
                        }));

                        // Store the organization options
                        yield put({
                            type: 'updateState',
                            payload: {
                                organizationOptions: options
                            }
                        });

                        // Set the organizations filter value
                        yield put({
                            type: 'updateState',
                            payload: {
                                filters: {
                                    ...((yield select((state: any) => state.people.filters)) || {}),
                                    organizations: organizationIds
                                }
                            }
                        });
                    }
                } else if (look_for === 'manufacturer') {
                    if (manufacturers && manufacturers.length > 0) {
                        // Create organization filter options from manufacturers
                        const organizationIds = manufacturers.map((manufacturer: any) => manufacturer.forager_id);

                        // Create options for display in the Select component
                        const options = manufacturers.map((manufacturer: any) => ({
                            value: manufacturer.forager_id,
                            label: manufacturer.name || manufacturer.forager_id,
                        }));

                        // Store the organization options
                        yield put({
                            type: 'updateState',
                            payload: {
                                organizationOptions: options
                            }
                        });

                        // Set the organizations filter value
                        yield put({
                            type: 'updateState',
                            payload: {
                                filters: {
                                    ...((yield select((state: any) => state.people.filters)) || {}),
                                    organizations: organizationIds
                                }
                            }
                        });
                    }
                }

                // If we have people data, update the state with it
                if (people && people.search_results.length > 0) {
                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: 1,
                            data: people.search_results,
                            total: people.total_search_results
                        }
                    });

                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: 1
                        }
                    });
                } else {
                    // If no people data came with the response, fetch people with the updated filters
                    yield put({
                        type: 'fetchPeople',
                        payload: {
                            page: 0
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching people from products:', error);
            }
        },
        *fetchPeopleFromProductsBoth({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                console.log('fetchPeopleFromProductsBoth', payload);
                const response: any = yield call(fetchPeopleFromProductsBoth, {
                    product_ids: payload
                });
                
                // Extract people, marketers, and manufacturers from the response
                const { people, marketers, manufacturers, summary } = response;
                
                console.log('Both response summary:', summary);

                // Combine marketers and manufacturers for organization options
                const allOrganizations = [];
                
                if (marketers && marketers.length > 0) {
                    const marketerOptions = marketers.map((marketer: any) => ({
                        value: marketer.forager_id,
                        label: marketer.name || marketer.domain || marketer.forager_id,
                        type: 'marketer'
                    }));
                    allOrganizations.push(...marketerOptions);
                }

                if (manufacturers && manufacturers.length > 0) {
                    const manufacturerOptions = manufacturers.map((manufacturer: any) => ({
                        value: manufacturer.forager_id,
                        label: manufacturer.name || manufacturer.forager_id,
                        type: 'manufacturer'
                    }));
                    allOrganizations.push(...manufacturerOptions);
                }

                // Remove duplicates (in case a company is both marketer and manufacturer)
                const uniqueOrganizations = allOrganizations.filter((org, index, arr) => 
                    arr.findIndex(item => item.value === org.value) === index
                );

                if (uniqueOrganizations.length > 0) {
                    // Store the organization options
                    yield put({
                        type: 'updateState',
                        payload: {
                            organizationOptions: uniqueOrganizations
                        }
                    });

                    // Set the organizations filter value
                    yield put({
                        type: 'updateState',
                        payload: {
                            filters: {
                                ...((yield select((state: any) => state.people.filters)) || {}),
                                organizations: uniqueOrganizations.map(org => org.value)
                            }
                        }
                    });
                }

                // If we have people data, update the state with it
                if (people && people.search_results.length > 0) {
                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: 1,
                            data: people.search_results,
                            total: people.total_search_results
                        }
                    });

                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: 1
                        }
                    });
                } else {
                    // If no people data came with the response, fetch people with the updated filters
                    yield put({
                        type: 'fetchPeople',
                        payload: {
                            page: 0
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching people from products (both):', error);
            }
        },
        *fetchCompanies(_: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                const response: any = yield call(request, `/people-v2/available_companies_for_client`);
                
                yield put({
                    type: 'setCompanies',
                    payload: (response as any).companies
                });
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        }
    },
    reducers: {
        updateState(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (Object.keys(payload).every(key => state[key as keyof PeopleModelState] === payload[key])) {
                return state;
            }
            return {
                ...state,
                ...payload
            };
        },
        updateSearchValue(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.searchValue === payload) {
                return state;
            }

            return {
                ...state,
                searchValue: payload,
            };
        },
        updateSearchValueAndOrganization(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            return {
                ...state,
                searchValue: payload.name,
                filters: {
                    ...state.filters,
                    organizations: [payload.foragerId],
                },
            };
        },
        updateSearchValueOnly(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.searchValue === payload) {
                return state;
            }
            return {
                ...state,
                searchValue: payload,
            };
        },
        updateSearchType(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.searchType === payload) {
                return state;
            }
            
            // Intelligent search type switching
            // When switching from 'search_company' to 'search_people':
            // - Clear the search value (company name shouldn't be used as person name)
            // - Keep the organization filter (so company appears in company filter)
            if (state.searchType === 'search_company' && payload === 'search_people') {
                return {
                    ...state,
                    searchType: payload,
                    searchValue: '', // Clear search value when switching to people search
                    // filters.organizations is preserved automatically
                };
            }
            
            // When switching from 'search_people' to 'search_company':
            // - Keep organization filter if it exists
            // - Set searchValue from organization filter if available
            if (state.searchType === 'search_people' && payload === 'search_company') {
                // If there's an organization filter, set it as the search value
                if (state.filters.organizations && state.filters.organizations.length > 0) {
                    const foragerId = state.filters.organizations[0];
                    const company = fixed_companies.find((c: any) => c.foragerId === foragerId);
                    if (company) {
                        return {
                            ...state,
                            searchType: payload,
                            searchValue: company.name,
                        };
                    }
                }
            }
            
            return {
                ...state,
                searchType: payload,
            };
        },
        setPersonsForPage(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const { page, data, total } = payload;

            return {
                ...state,
                peopleData: {
                    ...state.peopleData,
                    pages: {
                        ...state.peopleData.pages,
                        [page]: {
                            data,
                            total
                        }
                    },
                    total
                }
            };
        },
        resetFilters(state: PeopleModelState): PeopleModelState {
            return {
                ...state,
                filters: {},
                searchValue: '',
                peopleData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1
            };
        },
        toggleFilterDrawer(state: PeopleModelState): PeopleModelState {
            return {
                ...state,
                filterDrawerVisible: !state.filterDrawerVisible
            };
        },
        updateFilter(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const { field, value } = payload;

            if (state.filters[field as keyof Partial<PersonSearchRequest>] === value) {
                return state;
            }

            return {
                ...state,
                filters: {
                    ...state.filters,
                    [field]: value
                }
            };
        },
        addToSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.selectedRows.includes(payload)) {
                return state;
            }

            return {
                ...state,
                selectedRows: [...state.selectedRows, payload]
            };
        },
        removeFromSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (!state.selectedRows.includes(payload)) {
                return state;
            }

            return {
                ...state,
                selectedRows: state.selectedRows.filter(id => id !== payload)
            };
        },
        clearSelectedRows(state: PeopleModelState): PeopleModelState {
            if (state.selectedRows.length === 0) {
                return state;
            }

            return {
                ...state,
                selectedRows: []
            };
        },
        setSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const newSelectedRows = Array.isArray(payload) ? payload.map(Number) : [];
            
            if (JSON.stringify(state.selectedRows) === JSON.stringify(newSelectedRows)) {
                return state;
            }

            return {
                ...state,
                selectedRows: newSelectedRows
            };
        },
        setPeopleLists(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            return {
                ...state,
                peopleLists: payload
            };
        },
        toggleListModal(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const newValue = payload !== undefined ? payload : !state.listModalVisible;
            return {
                ...state,
                listModalVisible: newValue
            };
        },
        toggleCreateListModal(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const newValue = payload !== undefined ? payload : !state.createListModalVisible;
            return {
                ...state,
                createListModalVisible: newValue
            };
        },
        setAddingToList(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            return {
                ...state,
                addingToList: payload
            };
        },
        setCompanies(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            return {
                ...state,
                companies: payload
            };
        },
        resetAll(state: PeopleModelState): PeopleModelState {
            return {
                ...state,
                searchValue: '',
                searchType: 'search_people',
                filters: {},
                peopleData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1,
                pageSize: 10,
                selectedRows: [],
                filterDrawerVisible: false,
                peopleLists: [],
                listModalVisible: false,
                createListModalVisible: false,
                addingToList: false,
                organizationOptions: [],
                companies: [],
            }
        }
    }
}

export default Model;